import React, { useState } from 'react';
import { SEALS, DAY_ACCENT, SCORE_TIERS, getScoreTier, PROGRAM_DAY_THEMES, DAILY_ROUTINE_ACTIVITIES } from '../data/sealsData';

// ── Single scored seal circle ────────────────────────────────────────────────
function SealCircle({ seal, stamp, lang }) {
  const tier = stamp ? getScoreTier(stamp.score) : null;
  const earned = !!stamp;
  const accent = DAY_ACCENT[seal.day];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{
        position: 'relative', width: 80, height: 80, borderRadius: '50%',
        background: earned && tier ? tier.gradient : 'transparent',
        boxShadow: earned && tier ? `0 8px 28px ${tier.shadow}` : 'none',
        border: earned ? '3px solid rgba(255,255,255,0.5)' : `3px dashed ${accent}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s ease', overflow: 'hidden', flexShrink: 0,
      }}>
        {earned && <div style={{ position: 'absolute', inset: 5, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.25)', pointerEvents: 'none' }} />}
        {earned && <div style={{ position: 'absolute', top: '10%', left: '15%', width: '35%', height: '35%', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', filter: 'blur(4px)' }} />}
        {earned && tier
          ? <span style={{ fontSize: '2.2rem', position: 'relative', zIndex: 1 }}>{tier.emoji}</span>
          : <span style={{ fontSize: '1.4rem', opacity: 0.25 }}>🔒</span>}
      </div>

      {earned && stamp && (
        <div style={{ background: tier ? tier.gradient : '#E2E8F0', color: 'white', fontWeight: 900, fontSize: '0.72rem', padding: '2px 10px', borderRadius: 999, boxShadow: tier ? `0 2px 8px ${tier.shadow}` : 'none' }}>
          {stamp.score}%
        </div>
      )}

      <div style={{ fontSize: '0.62rem', fontWeight: 800, color: earned ? accent : '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {seal.session === 'morning' ? (lang === 'ja' ? '☀️ 午前' : '☀️ AM') : (lang === 'ja' ? '🌅 午後' : '🌅 PM')}
      </div>

      <div style={{ fontWeight: 900, fontSize: '0.68rem', color: earned ? '#1e293b' : '#cbd5e1', textAlign: 'center', lineHeight: 1.2, maxWidth: 80 }}>
        {earned && tier ? (lang === 'ja' ? tier.ja : tier.en) : (lang === 'ja' ? 'まだ未取得' : 'Not yet')}
      </div>

      {earned && stamp?.note && (
        <div style={{ background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, padding: '2px 7px', fontSize: '0.58rem', color: '#0e7490', fontWeight: 700, textAlign: 'center', maxWidth: 85, lineHeight: 1.3 }}>
          💬 {stamp.note}
        </div>
      )}
    </div>
  );
}

// ── Score Legend ─────────────────────────────────────────────────────────────
function ScoreLegend({ lang }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: '1rem' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#64748b', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        ℹ️ {lang === 'ja' ? 'シールの評価レベルを見る' : 'View seal score levels'} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ marginTop: 8, background: 'white', borderRadius: 16, padding: '1rem', border: '2px solid #F1F5F9', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
          {SCORE_TIERS.map(tier => (
            <div key={tier.en} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: tier.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0, boxShadow: `0 2px 8px ${tier.shadow}` }}>
                {tier.emoji}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '0.7rem', color: '#1e293b' }}>{lang === 'ja' ? tier.ja : tier.en}</div>
                <div style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700 }}>{tier.min}–{tier.max}%</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main SealPassport — shows seals for ONE program ─────────────────────────
// Props:
//   stamps       — all student stamps (filtered by program inside)
//   program      — the DB program object { id, name, name_ja, icon, color, slug }
//   allPrograms  — all enrolled programs (for the grand total bar)
//   studentName  — string
//   lang         — 'en' | 'ja'
//   threshold    — number (default 100)
// ── Routine activity colors/gradients ────────────────────────────────────────
const ROUTINE_STYLE = {
  cleaning:   { gradient: 'linear-gradient(135deg, #06B6D4, #0891B2)', shadow: '#06B6D460' },
  warmup:     { gradient: 'linear-gradient(135deg, #F59E0B, #EF4444)', shadow: '#F59E0B60' },
  brush:      { gradient: 'linear-gradient(135deg, #10B981, #059669)', shadow: '#10B98160' },
  boardgames: { gradient: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', shadow: '#8B5CF660' },
};

// ── Single routine sticker circle ─────────────────────────────────────────────
function RoutineCircle({ activity, done, lang }) {
  const style = ROUTINE_STYLE[activity.key] || ROUTINE_STYLE.cleaning;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{
        position: 'relative', width: 80, height: 80, borderRadius: '50%',
        background: done ? style.gradient : 'transparent',
        boxShadow: done ? `0 8px 28px ${style.shadow}` : 'none',
        border: done ? '3px solid rgba(255,255,255,0.5)' : `3px dashed ${style.gradient.match(/#[a-fA-F0-9]+/)?.[0] || '#94a3b8'}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s ease', overflow: 'hidden', flexShrink: 0,
      }}>
        {done && <div style={{ position: 'absolute', inset: 5, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.25)', pointerEvents: 'none' }} />}
        {done && <div style={{ position: 'absolute', top: '10%', left: '15%', width: '35%', height: '35%', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', filter: 'blur(4px)' }} />}
        <span style={{ fontSize: '2.2rem', position: 'relative', zIndex: 1, opacity: done ? 1 : 0.25 }}>{activity.emoji}</span>
      </div>
      <div style={{ fontSize: '0.62rem', fontWeight: 800, color: done ? style.shadow.replace('60', 'ff').slice(0, 7) : '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center', lineHeight: 1.2, maxWidth: 84 }}>
        {lang === 'ja' ? activity.ja : activity.en}
      </div>
      <div style={{ fontSize: '0.6rem', fontWeight: 700, color: done ? '#64748b' : '#e2e8f0', textAlign: 'center' }}>
        {done ? '✅' : (lang === 'ja' ? 'まだ未取得' : 'Not yet')}
      </div>
    </div>
  );
}

export default function SealPassport({ stamps = [], routineStamps = [], program, allPrograms = [], studentName, lang, threshold = 100, grandPct = 0 }) {
  // grandPct is computed once by the parent (StudentProfile) so this component
  // and the profile header never disagree on the student's overall completion.
  const unlocked = grandPct >= threshold;

  // Stamps for the selected program only
  const programStamps = program ? stamps.filter(s => s.program_id === program.id) : [];
  const programScore = programStamps.reduce((sum, s) => sum + (s.score || 0), 0);
  const programMax = 10 * 100; // 10 sessions × 100
  const programPct = Math.min(100, Math.round((programScore / programMax) * 100));

  const routineMaxCount = 5 * DAILY_ROUTINE_ACTIVITIES.length;
  const routinePct = routineMaxCount > 0 ? Math.min(100, Math.round((routineStamps.length / routineMaxCount) * 100)) : 0;

  // Day themes & accent colors for this program
  const themes = PROGRAM_DAY_THEMES[program?.slug] || PROGRAM_DAY_THEMES['kids-entrepreneurship'];
  const dayThemeLabels = lang === 'ja' ? themes.ja : themes.en;
  const dayAccentColors = themes.accent || DAY_ACCENT;

  return (
    <div>
      {/* ── Passport Cover ── */}
      <div style={{
        background: 'linear-gradient(135deg, #073B4C 0%, #0f5672 50%, #118AB2 100%)',
        borderRadius: 24, padding: 'clamp(1.25rem, 3vw, 2rem)',
        marginBottom: '1.25rem', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, flexShrink: 0, background: 'rgba(255,255,255,0.12)', border: '2px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem' }}>
            📕
          </div>
          <div style={{ flex: 1, color: 'white', minWidth: 0 }}>
            <div style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '3px', textTransform: 'uppercase', opacity: 0.55, marginBottom: 2 }}>
              {lang === 'ja' ? 'シールパスポート' : 'Seal Passport'}
            </div>
            <div style={{ fontWeight: 900, fontSize: 'clamp(1rem, 3vw, 1.3rem)', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {studentName}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: 0.65, fontWeight: 600 }}>
              {lang === 'ja' ? '琉球GLOCALキッズサマースクール 2026 · 沖縄' : 'Ryukyu GLOCAL Kids Summer School 2026 · Okinawa'}
            </div>
          </div>
          {/* Grand % circle */}
          <div style={{
            width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
            background: unlocked ? 'linear-gradient(135deg, #FFD166, #06D6A0)' : 'rgba(255,255,255,0.12)',
            border: '3px solid rgba(255,255,255,0.3)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            boxShadow: unlocked ? '0 8px 24px rgba(6,214,160,0.4)' : 'none',
          }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{grandPct}%</div>
            <div style={{ fontSize: '0.48rem', fontWeight: 800, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {lang === 'ja' ? '総合' : 'Total'}
            </div>
          </div>
        </div>

        {/* Grand progress bar */}
        <div style={{ position: 'relative', zIndex: 1, marginTop: '1rem' }}>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.12)', borderRadius: 999, overflow: 'hidden', position: 'relative' }}>
            <div style={{ height: '100%', width: `${grandPct}%`, background: unlocked ? 'linear-gradient(90deg, #06D6A0, #FFD166)' : 'linear-gradient(90deg, #FFD166, #FF6B35)', borderRadius: 999, transition: 'width 1s ease' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${threshold}%`, width: 2, background: 'rgba(255,255,255,0.6)', borderRadius: 1 }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, color: 'rgba(255,255,255,0.5)', fontSize: '0.58rem', fontWeight: 700 }}>
            <span>0%</span>
            <span style={{ color: 'rgba(255,255,255,0.7)' }}>
              🎓 {lang === 'ja' ? `修了証ライン: ${threshold}%` : `Certificate at ${threshold}%`}
            </span>
            <span>100%</span>
          </div>
          {unlocked && (
            <div style={{ textAlign: 'center', marginTop: 5, color: '#06D6A0', fontWeight: 900, fontSize: '0.82rem' }}>
              🏆 {lang === 'ja' ? '修了証が解放されました！🎉' : 'Certificate Unlocked! 🎉'}
            </div>
          )}
        </div>
      </div>

      <ScoreLegend lang={lang} />

      {/* ── Current Program Seals ── */}
      {program && program.slug !== 'daily-routine' ? (
        <div style={{ background: 'white', borderRadius: 20, border: '2px solid #FF6B3530', overflow: 'hidden', boxShadow: '0 4px 24px rgba(255,107,53,0.08)' }}>
          {/* Program header */}
          <div style={{ padding: '1rem 1.25rem', background: 'linear-gradient(135deg, #FFF8F0, white)', borderBottom: '1px solid #F8FAFC', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {program.icon && (
              <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: program.color || 'linear-gradient(135deg, #FF6B35, #FFD166)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                {program.icon}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 900, fontSize: '1rem', color: '#1e293b' }}>
                {lang === 'ja' ? (program.name_ja || program.name) : program.name}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, alignItems: 'center' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {lang === 'ja' ? `${programStamps.length}/10 セッション` : `${programStamps.length}/10 sessions`}
                </span>
                <span style={{ fontSize: '0.72rem', fontWeight: 900, color: programPct >= threshold ? '#06D6A0' : '#FF6B35' }}>{programPct}%</span>
              </div>
              <div style={{ height: 5, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden', marginTop: 4 }}>
                <div style={{ height: '100%', width: `${programPct}%`, background: programPct >= threshold ? 'linear-gradient(90deg, #06D6A0, #118AB2)' : 'linear-gradient(90deg, #FF6B35, #FFD166)', borderRadius: 999, transition: 'width 0.6s ease' }} />
              </div>
            </div>
          </div>

          {/* Day rows */}
          <div style={{ padding: '1rem 1.25rem 1.25rem' }}>
            {[1, 2, 3, 4, 5].map(dayNum => {
              const morningSeal = SEALS.find(s => s.day === dayNum && s.session === 'morning');
              const afternoonSeal = SEALS.find(s => s.day === dayNum && s.session === 'afternoon');
              const morningStamp = programStamps.find(s => s.day_number === dayNum && s.session === 'morning');
              const afternoonStamp = programStamps.find(s => s.day_number === dayNum && s.session === 'afternoon');
              const accent = dayAccentColors[dayNum] || DAY_ACCENT[dayNum];
              const hasAny = morningStamp || afternoonStamp;
              const dayAvg = hasAny ? Math.round(((morningStamp?.score || 0) + (afternoonStamp?.score || 0)) / 2) : 0;

              return (
                <div key={dayNum} style={{ marginBottom: dayNum < 5 ? '1rem' : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: hasAny ? accent : '#E2E8F0', color: hasAny ? 'white' : '#94a3b8', fontWeight: 900, fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: hasAny ? `0 3px 10px ${accent}50` : 'none' }}>
                      {dayNum}
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: hasAny ? accent : '#94a3b8', flex: 1 }}>
                      {dayThemeLabels[dayNum - 1]}
                    </span>
                    {hasAny && (
                      <span style={{ fontSize: '0.68rem', fontWeight: 900, color: accent, background: `${accent}15`, padding: '1px 8px', borderRadius: 999 }}>
                        ∅ {dayAvg}%
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, background: '#FAFBFC', borderRadius: 16, overflow: 'hidden', border: '1px solid #F1F5F9' }}>
                    {[
                      { seal: morningSeal, stamp: morningStamp },
                      { seal: afternoonSeal, stamp: afternoonStamp },
                    ].map(({ seal, stamp }, idx) => (
                      <div key={idx} style={{
                        padding: '1.1rem 0.75rem',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        borderLeft: idx === 1 ? '1px solid #F1F5F9' : 'none',
                        background: stamp ? `linear-gradient(180deg, ${DAY_ACCENT[seal.day]}06 0%, transparent 100%)` : 'transparent',
                      }}>
                        <SealCircle seal={seal} stamp={stamp} lang={lang} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👆</div>
          <div style={{ fontWeight: 700 }}>{lang === 'ja' ? 'プログラムを選んでください' : 'Select a program above to see seals'}</div>
        </div>
      )}

      {/* ── Daily Routine Stickers ── */}
      <div style={{ background: 'white', borderRadius: 20, border: '2px solid #06B6D420', overflow: 'hidden', boxShadow: '0 4px 24px rgba(6,182,212,0.06)', marginTop: '1.25rem' }}>
        {/* Header */}
        <div style={{ padding: '1rem 1.25rem', background: 'linear-gradient(135deg, #F0F9FF, white)', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #06B6D4, #0891B2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0, boxShadow: '0 4px 12px rgba(6,182,212,0.3)' }}>
            📋
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 900, fontSize: '1rem', color: '#1e293b' }}>
              {lang === 'ja' ? 'デイリールーティン' : 'Daily Routine'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                {routineStamps.length} / {5 * DAILY_ROUTINE_ACTIVITIES.length} {lang === 'ja' ? 'アクティビティ' : 'activities'}
              </span>
              <span style={{ fontSize: '0.72rem', fontWeight: 900, color: '#06B6D4' }}>
                {routinePct}%
              </span>
            </div>
            <div style={{ height: 5, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden', marginTop: 4 }}>
              <div style={{ height: '100%', width: `${routinePct}%`, background: 'linear-gradient(90deg, #06B6D4, #8B5CF6)', borderRadius: 999, transition: 'width 0.6s ease' }} />
            </div>
          </div>
        </div>

        {/* Day rows */}
        <div style={{ padding: '1rem 1.25rem 1.25rem' }}>
          {[1, 2, 3, 4, 5].map(dayNum => {
            const dayStamps = routineStamps.filter(s => s.day_number === dayNum);
            const accent = DAY_ACCENT[dayNum];
            const hasAny = dayStamps.length > 0;
            return (
              <div key={dayNum} style={{ marginBottom: dayNum < 5 ? '1rem' : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: hasAny ? accent : '#E2E8F0', color: hasAny ? 'white' : '#94a3b8', fontWeight: 900, fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: hasAny ? `0 3px 10px ${accent}50` : 'none' }}>
                    {dayNum}
                  </div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: hasAny ? accent : '#94a3b8' }}>
                    {lang === 'ja' ? `${dayNum}日目` : `Day ${dayNum}`}
                    <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.7 }}>
                      {dayStamps.length}/{DAILY_ROUTINE_ACTIVITIES.length}
                    </span>
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, background: '#FAFBFC', borderRadius: 16, overflow: 'hidden', border: '1px solid #F1F5F9' }}>
                  {DAILY_ROUTINE_ACTIVITIES.map((act, idx) => {
                    const done = dayStamps.some(s => s.session === act.session);
                    return (
                      <div key={act.key} style={{
                        padding: '1.1rem 0.5rem',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        borderLeft: idx > 0 ? '1px solid #F1F5F9' : 'none',
                      }}>
                        <RoutineCircle activity={act} done={done} lang={lang} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
